import React from 'react';
import { FormattedMessage } from 'react-intl';
import ControlledRadioGroup from 'components/common/form-controlled/controlled-radio-group';
import styled from 'types/theme.types';
import type { UseFormReturnType } from 'hooks/use-form.hook';
import { useFindVcsConnection } from 'stores/rq/vcs-connections';
import { useGetVcsDetails } from 'components/organizations/vcs/use-get-vcs-details';
import { Alert } from 'antd';
import { Skeleton } from 'components/common/skeleton';
import { buildLink } from 'components/common/link';
import { useCurrentOrganizationId } from 'hooks/use-current-organization-id';
import { useHasPermission } from 'hooks/use-has-permission';
import type { SchedulingApi } from '@env0/scheduling-service/api';
import type { BlueprintApi } from '@env0/blueprint-service/api';
import type { VcsApi } from '@env0/vcs-service/api';
import { Text } from 'components/common/form-components';

const RadioGroupContainer = styled.div`
  margin: 16px 0;
`;

const driftRemediationDisabled = 'DISABLED';

const itemsCloudToCode = [
  {
    value: driftRemediationDisabled,
    content: <Text id="drift-remediation.option.disabled" info={{ id: 'drift-remediation.option.disabled.info' }} />
  },
  {
    value: 'CODE_TO_CLOUD',
    content: (
      <Text id="drift-remediation.option.code-to-cloud" info={{ id: 'drift-remediation.option.code-to-cloud.info' }} />
    )
  },
  {
    value: 'CLOUD_TO_CODE',
    content: (
      <Text id="drift-remediation.option.cloud-to-code" info={{ id: 'drift-remediation.option.cloud-to-code.info' }} />
    )
  },
  {
    value: 'SMART_REMEDIATION',
    content: (
      <Text
        id="drift-remediation.option.smart-remediation"
        info={{ id: 'drift-remediation.option.smart-remediation.info' }}
      />
    )
  }
];

const items = [
  {
    value: driftRemediationDisabled,
    content: <Text id="drift-remediation.option.disabled" info={{ id: 'drift-remediation.option.disabled.info' }} />
  },
  {
    value: 'CODE_TO_CLOUD',
    content: (
      <Text id="drift-remediation.option.code-to-cloud" info={{ id: 'drift-remediation.option.code-to-cloud.info' }} />
    )
  }
];

const SUPPORTED_TEMPLATE_TYPES: BlueprintApi.BlueprintType[] = ['opentofu', 'terraform', 'terragrunt'];
const SUPPORTED_VCS_TYPES: VcsApi.VcsType[] = ['GitHub', 'GitHubEnterprise'];

const isTemplateTypeSupportedCloudToCode = (templateType: BlueprintApi.BlueprintType) =>
  SUPPORTED_TEMPLATE_TYPES.includes(templateType);

const isVcsTypeSupportedCloudToCode = (vcsType: VcsApi.VcsType) => SUPPORTED_VCS_TYPES.includes(vcsType);

export const ControlledDriftRemediationSettingsField: React.FC<{
  form: UseFormReturnType<SchedulingApi.ScheduledAction>;
  disabled?: boolean;
}> = ({ form, disabled }) => {
  const { isAuthorized } = useHasPermission('EDIT_ORGANIZATION_SETTINGS');
  const [environmentId] = form.useManualField<string>('environmentId');

  const isProjectPage = !environmentId;
  const organizationId = useCurrentOrganizationId();
  const { vcsType, repositoryUrl, templateType, isLoading } = useGetVcsDetails(environmentId);
  const isSupportedTemplateType = isTemplateTypeSupportedCloudToCode(templateType!);
  const isSupportedVcsType = isVcsTypeSupportedCloudToCode(vcsType!);
  const checkVcsConnectionHasCodeWrite = isSupportedTemplateType && isSupportedVcsType && !isProjectPage && !disabled;

  const { isFetched, data } = useFindVcsConnection(
    checkVcsConnectionHasCodeWrite,
    vcsType!,
    'CodeWrite',
    repositoryUrl
  );

  const value = disabled ? driftRemediationDisabled : form.watch('autoDriftRemediation');

  if (isLoading) {
    return <Skeleton active />;
  }

  const options = isProjectPage || isSupportedTemplateType ? itemsCloudToCode : items;

  return (
    <RadioGroupContainer>
      <ControlledRadioGroup
        form={form}
        name={'autoDriftRemediation'}
        items={options}
        disabled={disabled}
        value={value}
      />
      {checkVcsConnectionHasCodeWrite && !isFetched && <Skeleton active paragraph={false} />}
      {checkVcsConnectionHasCodeWrite && isFetched && !data?.id && (
        <Alert
          type="warning"
          message={
            <FormattedMessage
              id={`drift-remediation.option.cloud-to-code.${isAuthorized ? 'warning-admin' : 'warning'}`}
              values={buildLink(`/organizations/${organizationId}/vcs`, { openInNewWindow: true })}
            />
          }
        />
      )}
      {checkVcsConnectionHasCodeWrite && isFetched && data?.id && (
        <Alert
          type="success"
          message={
            <FormattedMessage
              id={`drift-remediation.option.cloud-to-code.write-access-exists`}
              values={{ repositoryUrl }}
            />
          }
        />
      )}
      {!isSupportedVcsType && !isProjectPage && (
        <Alert
          type="warning"
          message={
            <FormattedMessage
              id={`drift-remediation.option.cloud-to-code.pull-request-not-supported`}
              values={{ repositoryUrl }}
            />
          }
        />
      )}
      {isProjectPage && (
        <Alert
          type="warning"
          message={
            <>
              <FormattedMessage
                id={`drift-remediation.option.cloud-to-code.${isAuthorized ? 'warning-admin' : 'warning'}`}
                values={buildLink(`/organizations/${organizationId}/vcs`, { openInNewWindow: true })}
              />
              <br />
              <FormattedMessage
                id={`drift-remediation.option.cloud-to-code.supported-vcs`}
                values={buildLink(`/organizations/${organizationId}/vcs`, { openInNewWindow: true })}
              />
            </>
          }
        />
      )}
    </RadioGroupContainer>
  );
};
